<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import { Globals } from '@/modules/Globals';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    ILogicalPrinter,
    IUnresolvedV1Assignment,
  } from '@/stores/printing-v2.config';
  import { useV2PrintingUi } from '@/composition/printing/use-printing-v2-ui';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import V2PrinterCard from './V2PrinterCard.vue';
  import V2PrinterEditModal from './V2PrinterEditModal.vue';
  import V2PrinterSetupWizard from './wizard/V2PrinterSetupWizard.vue';
  import type { IV2HelpFlow } from './V2PrintingHelpTab.vue';

  const emit = defineEmits<{
    (e: 'open-help', flow: IV2HelpFlow): void;
  }>();

  const ui = useUi();
  const modals = useModal();
  const {
    v2Store,
    bridge,
    restaurantStore,
    isConnected,
    canEdit,
    printTestDocument,
  } = useV2PrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const downloadUrl = `${Globals.uploadsPath}/plugin2/QueRestoPrintBridge-Setup.exe`;

  const displayPrinters = computed<ILogicalPrinter[]>(
    () => v2Store.displayConfig?.logicalPrinters ?? []
  );
  const unresolved = computed<IUnresolvedV1Assignment[]>(
    () => v2Store.displayConfig?.unresolvedV1 ?? []
  );
  const hasCachedConfig = computed(
    () => displayPrinters.value.length > 0 || unresolved.value.length > 0
  );

  /** Install CTA only when nothing was ever configured on this machine. */
  const showInstallCta = computed(
    () => !isConnected.value && !hasCachedConfig.value
  );

  function destinationLabel(entry: IUnresolvedV1Assignment): string {
    if (entry.destination === 'ticket') return i18nT('printingV2.destTicket');
    if (entry.destination === 'sticker') return i18nT('printingV2.destSticker');
    return restaurantStore.stationName(entry.stationId ?? 0);
  }

  function openWizard(preTarget?: IUnresolvedV1Assignment) {
    modals.show(
      V2PrinterSetupWizard,
      { preTarget: preTarget ?? null },
      {
        adaptive: true,
        height: 'auto',
        width: 720,
        maxWidth: window.innerWidth * 0.95,
        scrollable: true,
        name: 'v2-printer-setup-wizard',
      },
      {}
    );
  }

  function openEdit(logical: ILogicalPrinter) {
    modals.show(
      V2PrinterEditModal,
      { logicalId: logical.id },
      {
        adaptive: true,
        height: 'auto',
        width: 560,
        maxWidth: window.innerWidth * 0.95,
        scrollable: true,
        name: 'v2-printer-edit-modal',
      },
      {}
    );
  }

  const testingIds = ref<number[]>([]);

  async function testPrinter(logical: ILogicalPrinter) {
    if (testingIds.value.includes(logical.id)) return;
    testingIds.value = [...testingIds.value, logical.id];
    try {
      await printTestDocument(logical, i18nT('printingV2.kindTest'));
      ui.showToast(AlertType.Success, i18nT('printingV2.testSent'));
    } catch (e) {
      console.error('[PrintingV2] test print failed', e);
      ui.showToast(AlertType.Error, i18nT('printingV2.testFailed'));
    } finally {
      testingIds.value = testingIds.value.filter((id) => id !== logical.id);
    }
  }

  async function removePrinter(logical: ILogicalPrinter) {
    const accepted = await modals.confirm({
      title: i18nT('printingV2.removeConfirmTitle'),
      message: i18nT('printingV2.removeConfirmBody', { name: logical.name }),
      acceptText: i18nT('printingV2.menuRemove'),
      acceptColor: 'error',
    });
    if (!accepted) return;

    const committed = await v2Store.mutateConfig((draft) => {
      draft.logicalPrinters = draft.logicalPrinters.filter(
        (lp) => lp.id !== logical.id
      );
      if (draft.assignments.ticket === logical.id) {
        draft.assignments.ticket = null;
      }
      if (draft.assignments.sticker === logical.id) {
        draft.assignments.sticker = null;
      }
      for (const key of Object.keys(draft.assignments.comandaByStation)) {
        const stationId = Number(key);
        if (draft.assignments.comandaByStation[stationId] === logical.id) {
          draft.assignments.comandaByStation = {
            ...draft.assignments.comandaByStation,
            [stationId]: null,
          };
        }
      }
    });
    if (!committed) return;

    // A manual (tcp/serial-bt) bridge entry no longer referenced by any
    // logical printer is removed from the bridge too.
    if (logical.transport !== 'spooler') {
      const stillReferenced = (v2Store.config?.logicalPrinters ?? []).some(
        (lp) => lp.ref === logical.ref
      );
      if (!stillReferenced) {
        try {
          await bridge.value?.removePrinter(logical.ref);
        } catch (e) {
          console.error('[PrintingV2] printers.remove failed', e);
        }
      }
    }
  }
</script>

<template>
  <div class="v2-printers-tab">
    <!-- Not installed: install CTA -->
    <div v-if="showInstallCta" class="printers-install">
      <PosUiEmptyState
        icon="printer"
        :title="$t('printingV2.installTitle')"
        :description="$t('printingV2.installDescription')"
      />
      <ol class="install-steps">
        <li class="install-step">
          <span class="install-step-number">1</span>
          {{ $t('printingV2.installStep1') }}
        </li>
        <li class="install-step">
          <span class="install-step-number">2</span>
          {{ $t('printingV2.installStep2') }}
        </li>
        <li class="install-step">
          <span class="install-step-number">3</span>
          {{ $t('printingV2.installStep3') }}
        </li>
      </ol>
      <a class="install-download" :href="downloadUrl" download>
        <PosUiButton color="primary" size="lg" icon="download">
          {{ $t('printingV2.downloadButton') }}
        </PosUiButton>
      </a>
      <div class="install-note">
        <PosUiIcon name="shield-alert-outline" class="install-note-icon" />
        {{ $t('printingV2.smartScreenNote') }}
      </div>
      <div class="install-note">
        <PosUiIcon name="lan-connect" class="install-note-icon" />
        {{ $t('printingV2.lnaNote') }}
      </div>
    </div>

    <template v-else>
      <!-- Disconnected with cached config: grayed grid -->
      <div v-if="!isConnected" class="printers-offline-hint">
        <PosUiIcon name="connection" class="printers-offline-hint-icon" />
        <span class="printers-offline-hint-text">
          {{ $t('printingV2.offlineHint') }}
        </span>
        <button
          class="printers-offline-hint-link"
          type="button"
          @click="emit('open-help', 'noconnect')"
        >
          {{ $t('printingV2.offlineHelpLink') }}
        </button>
      </div>

      <!-- Unresolved v1 migrations -->
      <div
        v-for="(entry, index) in unresolved"
        :key="`unresolved-${index}`"
        class="printers-reassign-card"
      >
        <div class="reassign-info">
          <div class="reassign-title">
            <PosUiIcon name="alert" class="reassign-title-icon" />
            {{ $t('printingV2.reassignTitle') }}
          </div>
          <div class="reassign-body">
            {{
              $t('printingV2.reassignBody', {
                name: entry.v1Name,
                destination: destinationLabel(entry),
              })
            }}
          </div>
        </div>
        <PosUiButton
          color="warning"
          variant="soft"
          size="sm"
          :disabled="!canEdit"
          @click="openWizard(entry)"
        >
          {{ $t('printingV2.reassignAction') }}
        </PosUiButton>
      </div>

      <!-- Printer grid -->
      <PosUiEmptyState
        v-if="!displayPrinters.length"
        icon="printer-off"
        :title="$t('printingV2.noPrintersTitle')"
        :description="$t('printingV2.noPrintersDescription')"
      >
        <template #action>
          <PosUiButton
            color="primary"
            icon="plus"
            :disabled="!canEdit"
            @click="openWizard()"
          >
            {{ $t('printingV2.addPrinter') }}
          </PosUiButton>
        </template>
      </PosUiEmptyState>

      <template v-else>
        <div class="printers-grid">
          <V2PrinterCard
            v-for="logical in displayPrinters"
            :key="logical.id"
            :logical="logical"
            :disabled="!canEdit"
            @test="testPrinter(logical)"
            @edit="openEdit(logical)"
            @remove="removePrinter(logical)"
          />
        </div>

        <div class="printers-actions">
          <PosUiButton
            color="primary"
            icon="plus"
            :disabled="!canEdit"
            @click="openWizard()"
          >
            {{ $t('printingV2.addPrinter') }}
          </PosUiButton>
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .v2-printers-tab {
    @apply tw-flex tw-flex-col tw-gap-3;

    .printers-install {
      @apply tw-flex tw-flex-col tw-items-center tw-gap-3 tw-pb-4;

      .install-steps {
        @apply tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0;

        .install-step {
          @apply tw-flex tw-items-center tw-gap-2.5 tw-text-sm tw-text-neutral-600;

          .install-step-number {
            @apply tw-flex tw-h-6 tw-w-6 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-bg-orange-100 tw-text-xs tw-font-bold tw-text-orange-700;
          }
        }
      }

      .install-download {
        @apply tw-mt-1 tw-no-underline;
      }

      .install-note {
        @apply tw-flex tw-max-w-md tw-items-start tw-gap-2 tw-rounded-lg tw-bg-neutral-50 tw-px-3 tw-py-2 tw-text-xs tw-text-neutral-500;

        .install-note-icon {
          @apply tw-mt-0.5 tw-flex-none tw-text-base tw-text-neutral-400;
        }
      }
    }

    .printers-offline-hint {
      @apply tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-neutral-100 tw-px-3 tw-py-2.5 tw-text-sm tw-text-neutral-600;

      .printers-offline-hint-icon {
        @apply tw-flex-none tw-text-lg tw-text-neutral-400;
      }

      .printers-offline-hint-text {
        @apply tw-flex-1;
      }

      .printers-offline-hint-link {
        @apply tw-flex-none tw-whitespace-nowrap tw-font-semibold tw-text-neutral-700 tw-underline tw-underline-offset-2 tw-transition-colors hover:tw-text-neutral-900;
      }
    }

    .printers-reassign-card {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-4 tw-py-3;

      .reassign-info {
        @apply tw-min-w-0;

        .reassign-title {
          @apply tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-amber-800;

          .reassign-title-icon {
            @apply tw-text-base;
          }
        }

        .reassign-body {
          @apply tw-mt-0.5 tw-text-xs tw-text-amber-700;
        }
      }
    }

    .printers-grid {
      @apply tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2;
    }

    .printers-actions {
      @apply tw-flex tw-justify-center tw-pt-1;
    }
  }
</style>
